import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, ExternalLink, Send } from 'lucide-react';
import { siteInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message envoyé avec succès', {
      description: 'Je vous répondrai dans les plus brefs délais.'
    });
    
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-24 md:pt-32 bg-white">
      {/* Header Section */}
      <section className="container-luxury mb-16 md:mb-24">
        <div className="max-w-2xl">
          <h1 className="section-title text-[#111111] mb-6 animate-fade-in-up">
            Contact
          </h1>
          <div className="w-16 h-px bg-[#111111] mb-8 animate-fade-in-up animation-delay-1" />
          <p className="body-text text-[#666666] animate-fade-in-up animation-delay-2">
            Parlons de votre projet. Je suis disponible pour des collaborations éditoriales, 
            des portraits artistiques et des directions créatives.
          </p>
        </div>
      </section>

      {/* Content Grid */}
      <section className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Info */}
          <div className="space-y-12">
            {/* Email */}
            <div className="animate-fade-in-up">
              <div className="flex items-center gap-4 mb-4">
                <Mail size={18} strokeWidth={1} className="text-[#111111]" />
                <span className="nav-link text-[#9A9A9A]">Email</span>
              </div>
              <a 
                href={`mailto:${siteInfo.email}`}
                className="font-serif text-xl text-[#111111] hover:opacity-70 transition-opacity duration-300"
              >
                {siteInfo.email}
              </a>
            </div>

            {/* Phone */}
            <div className="animate-fade-in-up animation-delay-1">
              <div className="flex items-center gap-4 mb-4">
                <Phone size={18} strokeWidth={1} className="text-[#111111]" />
                <span className="nav-link text-[#9A9A9A]">Téléphone</span>
              </div>
              <a 
                href={`tel:${siteInfo.phone}`}
                className="font-serif text-xl text-[#111111] hover:opacity-70 transition-opacity duration-300"
              >
                {siteInfo.phone}
              </a>
            </div>

            {/* Location */}
            <div className="animate-fade-in-up animation-delay-2">
              <div className="flex items-center gap-4 mb-4">
                <MapPin size={18} strokeWidth={1} className="text-[#111111]" />
                <span className="nav-link text-[#9A9A9A]">Localisation</span>
              </div>
              <p className="font-serif text-xl text-[#111111]">
                {siteInfo.location}
              </p>
            </div>

            {/* Social Links */}
            <div className="animate-fade-in-up animation-delay-3 pt-8 border-t border-[#EAEAEA]">
              <span className="nav-link text-[#9A9A9A] block mb-6">Réseaux Sociaux</span>
              <div className="flex items-center gap-8">
                <a
                  href={siteInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#111111] hover:opacity-70 transition-opacity duration-300"
                >
                  <Instagram size={20} strokeWidth={1} />
                  <span className="nav-link">Instagram</span>
                </a>
                <a
                  href={siteInfo.behance}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#111111] hover:opacity-70 transition-opacity duration-300"
                >
                  <ExternalLink size={20} strokeWidth={1} />
                  <span className="nav-link">Behance</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-fade-in-up animation-delay-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="nav-link text-[#9A9A9A] block mb-3">
                    Nom
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="nav-link text-[#9A9A9A] block mb-3">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="nav-link text-[#9A9A9A] block mb-3">
                  Sujet
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="L'objet de votre message"
                />
              </div>

              <div>
                <label htmlFor="message" className="nav-link text-[#9A9A9A] block mb-3">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="form-input resize-none"
                  placeholder="Parlez-moi de votre projet..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full sm:w-auto group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : (
                  <>
                    Envoyer
                    <Send size={14} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-24 md:h-32" />
    </div>
  );
};

export default ContactPage;
